/* eslint-disable @typescript-eslint/no-explicit-any */
import { PayloadAction } from '@reduxjs/toolkit'
import { all, delay, fork, put, race, select, take } from 'redux-saga/effects'
import { Map, MapDataset, SqlParameter } from 'features/maps/types'
import {
  checkDatasetUsesSqlParameter,
  getSqlParamtersPlaceholderSet,
  hasInvalidSqlPlaceholders
} from '../utils/queryParametersUtils'
import {
  loadSqlParameters,
  showGlobalBuilderError,
  updateDatasetsWithSqlParametersRequest,
  updateQueryRequest
} from './builderSlice'
import { migrateDatasetQueryFiltersToSqlParameters } from '../utils/dateFilterMigration'
import { removeSqlParameterCustomValues } from '../utils/sqlParameterUtils'

const UPDATE_DATASETS_WITH_SQL_PARAMETERS_DEBOUNCE_TIME = 750

function getAffectedDatasets(
  allSqlParametes: SqlParameter[],
  datasets: MapDataset[],
  updatedSqlParameter: SqlParameter | undefined
) {
  const validPlaceholders = getSqlParamtersPlaceholderSet(allSqlParametes)
  return datasets.filter((dataset) => {
    if (!dataset.queryTemplate) {
      return false
    }

    return updatedSqlParameter
      ? checkDatasetUsesSqlParameter(dataset.queryTemplate, updatedSqlParameter)
      : hasInvalidSqlPlaceholders(dataset.queryTemplate, validPlaceholders)
  })
}

function* updateDatasetsWithSqlParameters(
  actions: PayloadAction<{ parameter?: SqlParameter; skipSaveDataset?: boolean }>[]
) {
  try {
    const skipSaveDataset = actions.some((action) => action.payload.skipSaveDataset)

    const currentMapDatasets: MapDataset[] = yield select((state) => state.maps.currentMapDatasets)
    const sqlParameters: SqlParameter[] = yield select((state) => state.builder.sqlParameters)

    const datasetsToUpdate = new Set<MapDataset>()
    for (const action of actions) {
      const parameter = action.payload.parameter

      const affectedDatasets = getAffectedDatasets(sqlParameters, currentMapDatasets, parameter)

      for (const dataset of affectedDatasets) {
        datasetsToUpdate.add(dataset)
      }
    }

    const mapId: string = yield select((state) => state.maps.currentMap?.id)

    for (const dataset of datasetsToUpdate) {
      const datasetId = dataset.id
      if (!dataset.queryTemplate) {
        return
      }

      yield put(
        updateQueryRequest({
          mapId,
          datasetId,
          query: dataset.queryTemplate,
          sqlParameters,
          skipSaveDataset,
          createLayer: false
        })
      )
    }
  } catch (e: any) {
    yield put(showGlobalBuilderError(e.message))
  }
}

export function* getSavedSqlParameters() {
  try {
    const sqlParameters = yield select((state) => state.builder.sqlParameters)

    return sqlParameters.map((p) => removeSqlParameterCustomValues(p))
  } catch (e: any) {
    yield put(showGlobalBuilderError(e.message))
  }
}

export function* loadSqlParametersFromMap(currentMap: Map) {
  try {
    const sqlParameters = currentMap.keplerMapConfig?.config?.sqlParameters
    const sourcesQueryFilters = currentMap.keplerMapConfig?.config?.sourcesQueryFilters

    yield put(loadSqlParameters(sqlParameters || []))

    // TODO: this "frontend hot" migration can be removed once backend migrates all old maps with
    // `sourcesQueryFilters` to use sql parameters
    if (currentMap.id && sourcesQueryFilters) {
      yield migrateDatasetQueryFiltersToSqlParameters({ mapId: currentMap.id, sourcesQueryFilters })
    }
  } catch (e: any) {
    yield put(showGlobalBuilderError(e.message))
  }
}

/**
 * Like debouce, but collects all actions, so task see everything that happened during
 * debounce period.
 */
function* takeEveryWithDebounce(ms: number, pattern: string | string[], task) {
  yield fork(function* () {
    try {
      while (true) {
        const actions = [yield take(pattern)]

        while (true) {
          const { debounced, latestAction } = yield race({
            debounced: delay(ms),
            latestAction: take(pattern)
          })

          if (debounced) {
            try {
              yield fork(task, actions)
            } catch (error) {
              console.error(`takeAndDebounceWithAllActions ${task.name}`, error)
            }
            break
          } else {
            actions.push(latestAction)
          }
        }
      }
    } catch (error) {
      console.error(`debounceWithAllActions`, error)
    }
  })
}

function* watchMap() {
  yield takeEveryWithDebounce(
    UPDATE_DATASETS_WITH_SQL_PARAMETERS_DEBOUNCE_TIME,
    updateDatasetsWithSqlParametersRequest.type,
    updateDatasetsWithSqlParameters
  )
}

export default function* queryFiltersSagas() {
  try {
    yield all([watchMap()])
  } catch (e: any) {
    //TODO
  }
}
